import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Group, ShiftPatternList } from '@up2d8/contracts';
import { Button, EmptyState, Input, LoadingState, Select, useToast } from '@up2d8/ui';
import { ListError } from '../../shared/list-error';
import { useSession } from '../../shared/session';
import { QuietHoursCard } from './schedules-page';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/**
 * De tab "Ploegtijden en stille uren" bij een groep (Stap 14).
 *
 * De diensten van de groep - bij een lijn de diensten waar de ploegoverdracht mee werkt - en de
 * stille uren die voor de leden gelden. Een dienst weghalen zet hem uit: oude overdrachten
 * verwijzen ernaar.
 */
export function GroupShiftsSection({ groupId }: { groupId: string }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('06:00');
  const [endTime, setEndTime] = useState('14:00');
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [crew, setCrew] = useState('');

  const patterns = useQuery({
    queryKey: ['shift-patterns', groupId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/shift-patterns', {
        params: { query: { groupId } } as never,
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ShiftPatternList).items.filter((item) => item.groupId === groupId);
    },
  });
  const groups = useQuery({
    queryKey: ['directory', 'groups'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/groups');
      return (data as { items: Group[] } | undefined)?.items ?? [];
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/shift-patterns', {
        body: {
          groupId,
          name: name.trim(),
          startTime,
          endTime,
          daysOfWeek: days,
          crewGroupId: crew || null,
        } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['shift-patterns', groupId] });
    },
    onError: (error) =>
      toast.show({
        title:
          (error as { errors?: { message: string }[] }).errors?.[0]?.message ??
          t('scheduling.failed'),
        tone: 'danger',
      }),
  });

  const deactivate = useMutation({
    mutationFn: async (patternId: string) => {
      const { error } = await api.DELETE('/api/v1/shift-patterns/{patternId}', {
        params: { path: { patternId } },
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shift-patterns', groupId] }),
    onError: (error) =>
      toast.show({
        title:
          (error as { errors?: { message: string }[] }).errors?.[0]?.message ??
          t('scheduling.failed'),
        tone: 'danger',
      }),
  });

  return (
    <div className="flex flex-col gap-element" data-testid="group-shifts">
      {patterns.isPending ? (
        <LoadingState rows={2} label={t('common.loading')} />
      ) : patterns.isError ? (
        <ListError onRetry={() => void patterns.refetch()} error={patterns.error} />
      ) : patterns.data.length === 0 ? (
        <EmptyState title={t('scheduling.noShifts')} description={t('scheduling.noShiftsHint')} />
      ) : (
        <ul className="flex flex-col divide-y divide-border-light">
          {patterns.data.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-inline py-2">
              <span
                className={
                  item.active
                    ? 'text-content text-text-primary'
                    : 'text-content text-text-muted line-through'
                }
              >
                <span className="font-medium">{item.name}</span> · {item.startTime}–{item.endTime}
                {item.daysOfWeek.length < 7
                  ? ` · ${item.daysOfWeek.map((day) => t(`scheduling.dayShort${day}`)).join(' ')}`
                  : ''}
                {item.crewGroupName ? ` · ${item.crewGroupName}` : ''}
              </span>
              {item.active ? (
                <Button variant="ghost" size="sm" onClick={() => deactivate.mutate(item.id)}>
                  {t('scheduling.shiftOff')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-element rounded-card bg-background p-4">
        <Input
          label={t('scheduling.shiftName')}
          value={name}
          placeholder={t('scheduling.shiftNameHint')}
          onChange={(event) => setName(event.target.value)}
        />
        <div className="flex gap-element">
          <Input
            label={t('scheduling.from')}
            type="time"
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
          />
          <Input
            label={t('scheduling.until')}
            type="time"
            value={endTime}
            onChange={(event) => setEndTime(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('scheduling.days')}>
          {WEEKDAYS.map((day) => (
            <button
              key={day}
              type="button"
              aria-pressed={days.includes(day)}
              onClick={() =>
                setDays((current) =>
                  current.includes(day)
                    ? current.filter((value) => value !== day)
                    : [...current, day].sort(),
                )
              }
              className={
                days.includes(day)
                  ? 'rounded-pill bg-brand-primary px-3 py-1 text-caption font-semibold text-text-on-dark'
                  : 'rounded-pill border border-border px-3 py-1 text-caption font-semibold text-text-secondary'
              }
            >
              {t(`scheduling.dayShort${day}`)}
            </button>
          ))}
        </div>
        <Select
          label={t('scheduling.crew')}
          value={crew}
          onChange={(event) => setCrew(event.target.value)}
        >
          <option value="">{t('scheduling.crewNone')}</option>
          {(groups.data ?? [])
            .filter((group) => group.id !== groupId)
            .map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
        </Select>
        <Button
          variant="secondary"
          disabled={!name.trim() || days.length === 0}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          {t('scheduling.addShift')}
        </Button>
      </div>

      <QuietHoursCard scope="group" groupId={groupId} />
    </div>
  );
}
